import React, { useEffect, useState } from 'react';
import {
  Sparkles,
  ExternalLink,
  Lightbulb,
  MapPin,
  BookOpen,
  Globe,
  Wrench,
  ImageIcon,
  CalendarDays,
  Building2,
  ChevronDown,
} from 'lucide-react';

export default function InsightsDisplay({ insights, destination }) {
  if (!insights) return null;

  const hasDayPlans = insights.day_plans?.length > 0;
  const hasHighlights = insights.must_do_highlights?.length > 0;
  const hasNeighborhoods = insights.neighborhoods?.length > 0;
  const hasLinks = insights.useful_links?.length > 0;
  const hasTips = insights.tips?.length > 0;
  const hasLocalEvents = insights.local_events?.length > 0;

  return (
    <div className="space-y-4">
      {destination && <DestinationHero destination={destination} />}
      {hasLocalEvents && (
        <LocalEvents items={insights.local_events} />
      )}
      {hasDayPlans && (
        <DayPlans items={insights.day_plans} />
      )}
      {hasHighlights && (
        <MustDoHighlights items={insights.must_do_highlights} destination={destination} />
      )}
      {hasNeighborhoods && (
        <Neighborhoods items={insights.neighborhoods} />
      )}
      {hasLinks && (
        <UsefulLinks links={insights.useful_links} />
      )}
      {hasTips && (
        <Tips items={insights.tips} />
      )}
    </div>
  );
}

/* ─── Wikipedia lookup hook ─── */
function useWikipediaSummary(title) {
  const [data, setData] = useState({ thumbnail: null, url: null, loading: true });

  useEffect(() => {
    if (!title) {
      setData({ thumbnail: null, url: null, loading: false });
      return;
    }
    let cancelled = false;
    fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(title)}`)
      .then(r => (r.ok ? r.json() : null))
      .then(json => {
        if (cancelled) return;
        if (json && json.type !== 'disambiguation') {
          setData({
            thumbnail: json.thumbnail?.source || null,
            original: json.originalimage?.source || json.thumbnail?.source || null,
            extract: json.extract || null,
            url: json.content_urls?.desktop?.page || null,
            loading: false,
          });
        } else {
          setData({ thumbnail: null, original: null, extract: null, url: null, loading: false });
        }
      })
      .catch(() => { if (!cancelled) setData({ thumbnail: null, original: null, extract: null, url: null, loading: false }); });
    return () => { cancelled = true; };
  }, [title]);

  return data;
}

/* ─── Destination hero: a real photo of the place from Wikipedia ─── */
function DestinationHero({ destination }) {
  const { original, extract } = useWikipediaSummary(destination);
  if (!original) return null;
  return (
    <a
      href={`https://www.google.com/search?q=${encodeURIComponent(destination)}`}
      target="_blank"
      rel="noopener noreferrer"
      className="block relative rounded-2xl overflow-hidden h-40 group"
    >
      <img src={original} alt={destination} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <p className="text-[10px] font-bold uppercase tracking-widest text-white/60 mb-0.5">Discover</p>
        <p className="text-white font-black text-lg leading-tight">{destination}</p>
        {extract && <p className="text-white/75 text-xs line-clamp-2 mt-1">{extract}</p>}
      </div>
    </a>
  );
}

/* ─── Section 0: Day-by-Day Plans ─── */
function DayPlans({ items }) {
  const [openDay, setOpenDay] = useState(null);

  return (
    <section className="rounded-2xl border border-platinum-200/60 bg-white p-4">
      <SectionHeader icon={<CalendarDays size={15} className="text-coral-500" />} title="Day by Day" />
      <div className="mt-3 space-y-2">
        {items.sort((a, b) => a.day - b.day).map((plan) => (
          <div
            key={plan.day}
            className="rounded-xl border border-platinum-100 overflow-hidden"
          >
            {/* Header row — always visible */}
            <button
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-platinum-50 transition-colors"
              onClick={() => setOpenDay(openDay === plan.day ? null : plan.day)}
            >
              <div className="w-8 h-8 rounded-lg bg-coral-50 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold text-coral-500">{plan.day}</span>
              </div>
              <div className="flex-1 min-w-0">
                {plan.city && (
                  <p className="text-[10px] font-semibold text-platinum-400 uppercase tracking-wider leading-none mb-0.5">{plan.city}</p>
                )}
                <p className="text-sm font-semibold text-charcoal-600 leading-snug">{plan.theme || `Day ${plan.day}`}</p>
              </div>
              <span className={`text-platinum-400 text-lg transition-transform duration-150 flex-shrink-0 ${openDay === plan.day ? 'rotate-180' : ''}`}>
                ›
              </span>
            </button>

            {/* Expanded detail */}
            {openDay === plan.day && (
              <div className="px-4 pb-4 pt-1 border-t border-platinum-100 space-y-3 bg-platinum-50/40">
                {plan.why && (
                  <div>
                    <p className="text-[10px] font-bold text-charcoal-400 uppercase tracking-wider mb-1">Why these activities</p>
                    <p className="text-sm text-charcoal-500 leading-relaxed">{plan.why}</p>
                  </div>
                )}
                {plan.neighborhood_deep_dive && (
                  <div>
                    <div className="flex items-center gap-1.5 mb-1">
                      <Building2 size={11} className="text-columbia-500" />
                      <p className="text-[10px] font-bold text-charcoal-400 uppercase tracking-wider">The neighbourhood</p>
                    </div>
                    <p className="text-sm text-charcoal-500 leading-relaxed">{plan.neighborhood_deep_dive}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─── Section 1: Must-Do Highlights ─── */
function MustDoHighlights({ items, destination }) {
  return (
    <section className="rounded-2xl border border-platinum-200/60 bg-white p-4">
      <SectionHeader icon={<Sparkles size={15} className="text-coral-500" />} title="Must-Do Highlights" />
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {items.map((h, i) => (
          <HighlightCard key={i} highlight={h} destination={destination} />
        ))}
      </div>
    </section>
  );
}

function HighlightCard({ highlight, destination }) {
  const { thumbnail, loading } = useWikipediaSummary(highlight.title);
  const linkHref = `https://www.google.com/search?q=${encodeURIComponent(`${highlight.title}${destination ? ' ' + destination : ''}`)}`;
  const isHidden = highlight.type === 'hidden_gem';

  return (
    <a
      href={linkHref}
      target="_blank"
      rel="noopener noreferrer"
      className="group block rounded-xl border border-platinum-100 bg-white overflow-hidden hover:shadow-md hover:border-coral-200 transition-all"
    >
      <div className="relative h-24 bg-gradient-to-br from-platinum-100 to-platinum-200 overflow-hidden">
        {loading ? (
          <div className="w-full h-full bg-platinum-100 animate-pulse" />
        ) : thumbnail ? (
          <img src={thumbnail} alt={highlight.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon size={22} className="text-platinum-300" />
          </div>
        )}
        <span className={`absolute top-2 left-2 text-[9px] font-semibold px-1.5 py-0.5 rounded-full uppercase tracking-wide backdrop-blur-sm ${isHidden ? 'bg-naples-100/90 text-naples-700' : 'bg-columbia-100/90 text-columbia-700'}`}>
          {isHidden ? 'Hidden Gem' : 'Iconic'}
        </span>
      </div>
      <div className="p-3">
        <div className="flex items-start justify-between gap-1.5 mb-1">
          <h4 className="text-xs font-semibold text-charcoal-600 leading-tight group-hover:text-coral-600 transition-colors">{highlight.title}</h4>
          <ExternalLink size={11} className="text-platinum-400 group-hover:text-coral-500 flex-shrink-0 mt-0.5" />
        </div>
        <p className="text-[11px] text-platinum-600 leading-snug line-clamp-2">{highlight.description}</p>
      </div>
    </a>
  );
}

/* ─── Section 2: Neighborhoods ─── */
function Neighborhoods({ items }) {
  // Group by city
  const byCityMap = {};
  items.forEach(n => {
    const city = n.city || 'General';
    if (!byCityMap[city]) byCityMap[city] = [];
    byCityMap[city].push(n);
  });
  const cities = Object.keys(byCityMap);
  const isMulti = cities.length > 1;

  return (
    <CollapsibleSection icon={<MapPin size={15} className="text-columbia-500" />} title="Neighbourhoods to Explore">
      <div className="space-y-4">
        {cities.map(city => (
          <div key={city}>
            {isMulti && (
              <p className="text-[10px] font-bold text-charcoal-400 uppercase tracking-widest mb-2">{city}</p>
            )}
            <div className="space-y-2">
              {byCityMap[city].map((n, i) => (
                <a
                  key={i}
                  href={`https://www.google.com/search?q=${encodeURIComponent(n.name + ' ' + city)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-start gap-3 px-3 py-2.5 rounded-xl border border-platinum-100 hover:border-columbia-200 hover:bg-columbia-50/30 transition-all"
                >
                  <div className="w-7 h-7 rounded-lg bg-columbia-50 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-columbia-100 transition-colors">
                    <MapPin size={12} className="text-columbia-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-bold text-charcoal-600 group-hover:text-columbia-700 transition-colors">{n.name}</span>
                      <ExternalLink size={10} className="text-platinum-300 group-hover:text-columbia-400 flex-shrink-0" />
                    </div>
                    <p className="text-[11px] text-platinum-600 leading-snug mb-1">{n.vibe}</p>
                    <span className="inline-block text-[10px] font-semibold text-columbia-600 bg-columbia-50 px-2 py-0.5 rounded-full">{n.best_for}</span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>
    </CollapsibleSection>
  );
}

/* ─── Section 3: Useful Links (blogs + resources) ─── */
const LINK_TYPE_CONFIG = {
  blog:     { icon: BookOpen, label: 'Blog',     className: 'bg-coral-50 text-coral-600 border-coral-200' },
  official: { icon: Globe,    label: 'Official', className: 'bg-columbia-50 text-columbia-700 border-columbia-200' },
  resource: { icon: Wrench,   label: 'Resource', className: 'bg-naples-50 text-naples-700 border-naples-300' },
};

function UsefulLinks({ links }) {
  const blogs = links.filter(l => l.type === 'blog');
  const others = links.filter(l => l.type !== 'blog');

  return (
    <CollapsibleSection icon={<BookOpen size={15} className="text-coral-500" />} title="Travel Blogs & Resources">
      <div className="space-y-4">
        {blogs.length > 0 && (
          <div>
            <p className="text-[10px] font-bold text-charcoal-400 uppercase tracking-widest mb-2">Travel Blogs</p>
            <div className="space-y-2">
              {blogs.map((link, i) => <LinkRow key={i} link={link} />)}
            </div>
          </div>
        )}
        {others.length > 0 && (
          <div>
            <p className="text-[10px] font-bold text-charcoal-400 uppercase tracking-widest mb-2">Official & Resources</p>
            <div className="space-y-2">
              {others.map((link, i) => <LinkRow key={i} link={link} />)}
            </div>
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
}

function LinkRow({ link }) {
  const cfg = LINK_TYPE_CONFIG[link.type] || LINK_TYPE_CONFIG.resource;
  const Icon = cfg.icon;
  return (
    <a
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-start gap-3 px-3 py-2.5 rounded-xl border border-platinum-100 hover:border-platinum-200 hover:bg-platinum-50 transition-all group"
    >
      <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 border ${cfg.className}`}>
        <Icon size={11} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-xs font-semibold text-charcoal-600 group-hover:text-charcoal-700 transition-colors truncate">{link.title}</span>
          <ExternalLink size={10} className="text-platinum-300 group-hover:text-charcoal-400 flex-shrink-0" />
        </div>
        <p className="text-[11px] text-platinum-500 leading-snug line-clamp-1">{link.description}</p>
      </div>
    </a>
  );
}

/* ─── Section 4: Tips ─── */
function Tips({ items }) {
  return (
    <CollapsibleSection icon={<Lightbulb size={15} className="text-naples-600" />} title="Local Tips">
      <ul className="space-y-2">
        {items.map((tip, i) => (
          <li key={i} className="flex gap-2.5 text-xs text-charcoal-600 leading-snug">
            <span className="mt-1.5 block w-1.5 h-1.5 rounded-full bg-naples-400 flex-shrink-0" />
            <span>{tip}</span>
          </li>
        ))}
      </ul>
    </CollapsibleSection>
  );
}

/* ─── Section 5: Local Events & Festivals ─── */
const EVENT_TYPE_CONFIG = {
  festival: { label: 'Festival', className: 'bg-coral-50 text-coral-600 border-coral-200' },
  seasonal: { label: 'Seasonal', className: 'bg-naples-50 text-naples-700 border-naples-300' },
  cultural: { label: 'Cultural', className: 'bg-columbia-50 text-columbia-700 border-columbia-200' },
  nightlife: { label: 'Nightlife', className: 'bg-charcoal-100 text-charcoal-700 border-charcoal-200' },
  food: { label: 'Food', className: 'bg-naples-50 text-naples-800 border-naples-300' },
};

function LocalEvents({ items }) {
  return (
    <section className="rounded-2xl border border-platinum-200/60 bg-white p-4">
      <SectionHeader icon={<CalendarDays size={15} className="text-coral-500" />} title="What's On During Your Trip" />
      <div className="mt-3 space-y-2.5">
        {items.map((event, i) => {
          const cfg = EVENT_TYPE_CONFIG[event.type] || EVENT_TYPE_CONFIG.cultural;
          return (
            <div key={i} className="flex gap-3 px-3 py-3 rounded-xl border border-platinum-100 bg-platinum-50/30">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-xs font-bold text-charcoal-600">{event.name}</span>
                  <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full border flex-shrink-0 ${cfg.className}`}>
                    {cfg.label}
                  </span>
                  {event.when && (
                    <span className="text-[10px] text-platinum-400 font-medium flex-shrink-0">{event.when}</span>
                  )}
                </div>
                <p className="text-[11px] text-platinum-600 leading-snug mb-1.5">{event.description}</p>
                {event.relevance && (
                  <p className="text-[11px] text-charcoal-500 italic leading-snug border-l-2 border-coral-200 pl-2">{event.relevance}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ─── Shared section header ─── */
function SectionHeader({ icon, title }) {
  return (
    <div className="flex items-center gap-2">
      {icon}
      <h3 className="text-sm font-bold text-charcoal-600">{title}</h3>
    </div>
  );
}

/* ─── Collapsed-by-default card shell for secondary/reference content, so the page
   isn't a wall of equally-weighted cards — headline sections (events, day plans,
   highlights) stay expanded; this is for the "more if you want it" stuff. ─── */
function CollapsibleSection({ icon, title, children }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="rounded-2xl border border-platinum-200/60 bg-white overflow-hidden">
      <button
        className="w-full flex items-center justify-between gap-2 p-4 text-left"
        onClick={() => setOpen(o => !o)}
      >
        <SectionHeader icon={icon} title={title} />
        <ChevronDown size={14} className={`text-platinum-400 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </section>
  );
}
